/**
 * TemplateItemRows — the item lists of the Admin template modals: the
 * Products and Procedures of a membership or package (MembershipTemplateForm)
 * and the Procedures, Products and Discounts of a quote preset
 * (QuotePresetFormModal), so both read the same.
 *
 *   - ItemsBox: the bordered list with its label and Add button.
 *   - ItemRow: corp's accordion row — a shaded header (red while nothing is
 *     picked) with the title and a short summary, a note on the right and
 *     remove; a click opens the row's fields.
 *   - ProcedureFields: a procedure's fields, as legacy's
 *     (lib/admin/membership-procedures-display.php).
 *
 * On a Locked page (inside AdminFormModal's locked body) nothing is added or
 * removed, and rows still open to read.
 */
import { type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { bandFor } from '@/lib/procedureHours';
import { cn } from '@/lib/utils';
import type { ProcedureCategoryOption } from '@/services/membershipTemplates.service';
import { DialogField, LABEL_CLASS, fieldClasses } from './DialogChrome';

export function ItemsBox({
  label,
  addLabel,
  warn = false,
  badge,
  onAdd,
  children,
}: {
  label: string;
  addLabel: string;
  /** Amber, with "No products or procedures selected" (a membership or package needs one). */
  warn?: boolean;
  badge?: ReactNode;
  onAdd: () => void;
  children: ReactNode;
}) {
  // Locked (inside a locked modal body): nothing is added.
  const locked = useFieldLocked();
  return (
    <section
      aria-label={label}
      className={cn('space-y-2 rounded-md border p-3', warn ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200')}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={LABEL_CLASS}>
          <span>{label}</span>
          {badge}
        </span>
        {!locked && (
          <Button variant="secondary" size="xs" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={onAdd}>
            {addLabel}
          </Button>
        )}
      </div>
      {warn && <p className={fieldClasses.message('warning')}>No products or procedures selected</p>}
      <div className="space-y-2">{children}</div>
    </section>
  );
}

/**
 * One product, procedure or discount: corp's accordion row — a shaded header
 * (red while nothing is picked) with the title and summary on the left, the
 * `aside` note (a product's expiry, a line's price) and remove on the right;
 * a click on it opens the fields.
 */
export function ItemRow({
  title,
  missing,
  meta,
  aside,
  open,
  onToggle,
  removeLabel,
  onRemove,
  children,
}: {
  title: string | null;
  missing: string;
  meta?: string | null;
  aside?: string | null;
  open: boolean;
  onToggle: () => void;
  removeLabel: string;
  onRemove: () => void;
  children: ReactNode;
}) {
  // Locked: the row still opens to read, but is never removed.
  const locked = useFieldLocked();
  return (
    <div className={cn('overflow-hidden rounded-md border bg-white', title ? 'border-slate-200' : 'border-red-400')} data-testid="template-item-row">
      <div className={cn('flex items-center gap-2 px-3 py-2 transition-colors', title ? 'bg-slate-50 hover:bg-slate-100' : 'bg-red-50 hover:bg-red-100')}>
        <button type="button" aria-expanded={open} onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />}
          {title ? (
            <span className="min-w-0 truncate text-sm font-medium text-slate-900" title={title}>
              {title}
            </span>
          ) : (
            <span className="text-sm italic text-red-600">{missing}</span>
          )}
          {meta && <span className="shrink-0 whitespace-nowrap text-xs text-slate-500">{meta}</span>}
          {aside && <span className="ml-auto shrink-0 whitespace-nowrap text-xs text-slate-500">{aside}</span>}
        </button>
        {!locked && (
          <Button variant="ghost" size="xs" iconOnly aria-label={removeLabel} leftIcon={<Trash2 className="h-3.5 w-3.5 text-red-500" />} onClick={onRemove} />
        )}
      </div>
      {open && <div className="border-t border-slate-200 p-3">{children}</div>}
    </div>
  );
}

/** What ProcedureFields reads and writes on a procedure row. */
export interface ProcedureFieldsRow {
  key: string;
  categoryID: number | null;
  /** Sub category: legacy's `procID`. */
  procID: number | null;
  procSubID: number | null;
  /** The saved names, shown when the catalog no longer lists them (retired). */
  savedName: string;
  savedCategoryName: string;
  savedSubcategoryName: string;
  description: string;
  hours: number;
  price: string;
  effectivePrice: string;
}

/** A procedure row's title: its catalog name, else the saved one; null while none is picked. */
export function procedureNameOf(row: Pick<ProcedureFieldsRow, 'procSubID' | 'savedName'>, tree: ProcedureCategoryOption[]): string | null {
  if (!row.procSubID) return null;
  for (const c of tree) {
    for (const s of c.subcategories) {
      const p = s.procedures.find((x) => x.procSubID === row.procSubID);
      if (p) return p.name;
    }
  }
  return row.savedName || 'Procedure';
}

/**
 * A procedure row's fields, as legacy's (lib/admin/membership-procedures-display.php):
 * Category → Sub Category → Procedure stacked with the Description beside
 * them, then Hours, Normal and Effective price. A new category or sub category
 * clears what was picked below it. Picking the procedure sets both prices to
 * its catalog price, Hours to its band and the Description to its layman's
 * terms, as legacy's updateQuoteInfo does. A saved row on a since-retired
 * entry keeps it as a choice, marked inactive.
 */
export function ProcedureFields({
  row,
  tree,
  hourBands,
  onPatch,
}: {
  row: ProcedureFieldsRow;
  tree: ProcedureCategoryOption[];
  hourBands: number[];
  onPatch: (next: Partial<ProcedureFieldsRow>) => void;
}) {
  const category = tree.find((c) => c.categoryID === row.categoryID) ?? null;
  const subcategory = category?.subcategories.find((s) => s.procID === row.procID) ?? null;
  const procedures = subcategory?.procedures ?? [];

  const categoryOptions = tree.map((c) => ({ value: c.categoryID, label: c.name }));
  if (row.categoryID && !category) categoryOptions.push({ value: row.categoryID, label: `${row.savedCategoryName || 'Category'} (inactive)` });
  const subcategoryOptions = (category?.subcategories ?? []).map((s) => ({ value: s.procID, label: s.name }));
  if (row.procID && !subcategory) subcategoryOptions.push({ value: row.procID, label: `${row.savedSubcategoryName || 'Sub category'} (inactive)` });
  const procedureOptions = procedures.map((p) => ({ value: p.procSubID, label: p.name }));
  if (row.procSubID && !procedures.some((p) => p.procSubID === row.procSubID)) {
    procedureOptions.push({ value: row.procSubID, label: `${row.savedName || 'Procedure'} (inactive)` });
  }

  // A saved Hours value that is not on the scale stays choosable, marked, rather
  // than silently snapping to another band.
  const offScale = row.hours > 0 && !hourBands.includes(row.hours) ? row.hours : null;
  const hourOptions = [
    { value: '0', label: '0' },
    ...hourBands.map((b) => ({ value: String(b), label: b.toFixed(2) })),
    ...(offScale !== null ? [{ value: String(offScale), label: `${offScale.toFixed(2)} (not on this scale)` }] : []),
  ];

  function pickProcedure(id: number | null) {
    const p = procedures.find((x) => x.procSubID === id);
    if (!p) {
      onPatch({ procSubID: id });
      return;
    }
    onPatch({
      procSubID: id,
      price: String(p.feeToPatient),
      effectivePrice: String(p.feeToPatient),
      hours: bandFor(p.expNumHours, hourBands),
      description: p.laymansTerms,
    });
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="min-w-0 space-y-3">
          <DialogField label="Category" htmlFor={`${row.key}-proc-category`}>
            <SearchableCombobox<number>
              id={`${row.key}-proc-category`}
              options={categoryOptions}
              value={row.categoryID}
              onChange={(v) => v !== row.categoryID && onPatch({ categoryID: v ?? null, procID: null, procSubID: null })}
              placeholder="Select category"
            />
          </DialogField>
          <DialogField label="Sub Category" htmlFor={`${row.key}-proc-subcategory`}>
            <SearchableCombobox<number>
              id={`${row.key}-proc-subcategory`}
              options={subcategoryOptions}
              value={row.procID}
              onChange={(v) => v !== row.procID && onPatch({ procID: v ?? null, procSubID: null })}
              placeholder={row.categoryID ? 'Select sub category' : 'Select a category first'}
              disabled={!row.categoryID}
            />
          </DialogField>
          <DialogField label="Procedure" htmlFor={`${row.key}-procedure`} state={row.procSubID ? 'none' : 'error'}>
            <SearchableCombobox<number>
              id={`${row.key}-procedure`}
              options={procedureOptions}
              value={row.procSubID}
              onChange={(v) => v !== row.procSubID && pickProcedure(v ?? null)}
              placeholder={row.procID ? 'Select procedure' : 'Select a sub category first'}
              disabled={!row.procID}
            />
          </DialogField>
        </div>
        <DialogField label="Description" htmlFor={`${row.key}-proc-description`} className="content-start">
          <Textarea autoResize id={`${row.key}-proc-description`} rows={3} value={row.description} onChange={(e) => onPatch({ description: e.target.value })} />
        </DialogField>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <DialogField label="Hours" htmlFor={`${row.key}-hours`}>
          <Select<string> id={`${row.key}-hours`} options={hourOptions} value={String(row.hours)} onChange={(v) => onPatch({ hours: Number(v) || 0 })} />
        </DialogField>
        <DialogField label="Normal price" htmlFor={`${row.key}-price`}>
          <Input id={`${row.key}-price`} type="number" step="0.01" min="0" leftAddon="$" value={row.price} onChange={(e) => onPatch({ price: e.target.value })} />
        </DialogField>
        <DialogField label="Effective price" htmlFor={`${row.key}-effective`}>
          <Input
            id={`${row.key}-effective`}
            type="number"
            step="0.01"
            min="0"
            leftAddon="$"
            value={row.effectivePrice}
            onChange={(e) => onPatch({ effectivePrice: e.target.value })}
          />
        </DialogField>
      </div>
    </div>
  );
}
